import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { Plus, Eye, CheckCircle, Clock, Send as SendIcon } from 'lucide-react';
import WeeklyReflectionEditor from './WeeklyReflectionEditor';
import ReflectionResponseViewer from './ReflectionResponseViewer';
import ReflectionAnswerForm from './ReflectionAnswerForm';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function WeeklyReflectionManager({ clientId, clientName, isClientView = false }) {
  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list', 'create', 'edit', 'viewResponse'
  const [selectedReflectionId, setSelectedReflectionId] = useState(null);

  useEffect(() => {
    fetchReflections();
  }, [clientId]);

  const fetchReflections = async () => {
    setLoading(true);
    try {
      // Use client-specific endpoint if in client view
      const endpoint = isClientView
        ? `${API_URL}/client/weekly-reflections`
        : `${API_URL}/clients/${clientId}/weekly-reflections`;
      const response = await axios.get(endpoint);
      setReflections(response.data);
    } catch (error) {
      console.error('Error fetching reflections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewResponse = async (reflectionId) => {
    // Check if there's a response for this reflection
    try {
      await axios.get(`${API_URL}/weekly-reflections/${reflectionId}/responses`);
      setSelectedReflectionId(reflectionId);
      setView('viewResponse');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.success('Client has not submitted responses yet.');
      } else {
        console.error('Error checking responses:', error);
        toast.error('Error loading responses.');
      }
    }
  };

  const getStatusBadge = (reflection) => {
    // For client view, show completion status instead of draft/sent
    if (isClientView) {
      if (reflection.hasResponse) {
        return (
          <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)', color: '#10B981' }}>
            <CheckCircle className="w-3 h-3" />
            Completed
          </span>
        );
      } else {
        return (
          <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[var(--accent)]/20 text-[var(--accent)]">
            <Clock className="w-3 h-3" />
            Awaiting Response
          </span>
        );
      }
    }

    // For coach view, show draft/sent status
    if (reflection.status === 'draft') {
      return (
        <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-tertiary)' }}>
          <Clock className="w-3 h-3" />
          Draft
        </span>
      );
    }
    if (reflection.status === 'sent') {
      return (
        <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[var(--accent)]/20 text-[var(--accent)]">
          <SendIcon className="w-3 h-3" />
          Sent
        </span>
      );
    }
    return null;
  };

  if (view === 'create' || (view === 'edit' && selectedReflectionId)) {
    // For client view, use ReflectionAnswerForm to answer questions
    if (isClientView && view === 'edit') {
      return (
        <div>
          <button
            onClick={() => {
              setView('list');
              setSelectedReflectionId(null);
            }}
            className="mb-6 flex items-center gap-2 transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            ← Back to Reflections
          </button>
          <ReflectionAnswerForm
            reflectionId={selectedReflectionId}
            onClose={() => {
              setView('list');
              setSelectedReflectionId(null);
              fetchReflections();
            }}
          />
        </div>
      );
    }

    // For coach view, use WeeklyReflectionEditor
    return (
      <WeeklyReflectionEditor
        clientId={clientId}
        clientName={clientName}
        reflectionId={view === 'edit' ? selectedReflectionId : null}
        onClose={() => {
          setView('list');
          setSelectedReflectionId(null);
          fetchReflections();
        }}
        onSent={() => {
          setView('list');
          setSelectedReflectionId(null);
          fetchReflections();
        }}
      />
    );
  }

  if (view === 'viewResponse' && selectedReflectionId) {
    return (
      <div>
        <button
          onClick={() => {
            setView('list');
            setSelectedReflectionId(null);
          }}
          className="mb-6 flex items-center gap-2 transition-colors"
          style={{ color: 'var(--text-tertiary)' }}
          onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
          onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-tertiary)'}
        >
          ← Back to Reflections
        </button>
        <ReflectionResponseViewer
          reflectionId={selectedReflectionId}
          onClose={() => {
            setView('list');
            setSelectedReflectionId(null);
          }}
        />
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Weekly Reflections
          </h2>
          <p className="mt-1" style={{ color: 'var(--text-tertiary)' }}>
            {isClientView ? 'Your reflection questions from your coach' : `For ${clientName}`}
          </p>
        </div>
        {!isClientView && (
          <button
            onClick={() => setView('create')}
            className="px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2"
            style={{ backgroundColor: 'var(--accent)', color: '#ffffff' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--accent-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--accent)'}
          >
            <Plus className="w-5 h-5" />
            Generate New Reflection
          </button>
        )}
      </div>

      {/* Reflections List */}
      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-[var(--accent)]/30 border-t-[var(--accent)] mx-auto mb-4"></div>
            <p style={{ color: 'var(--text-tertiary)' }}>Loading reflections...</p>
          </div>
        </div>
      ) : reflections.length === 0 ? (
        <div className="rounded-lg p-12 text-center" style={{ backgroundColor: 'var(--bg-secondary)', borderWidth: '1px', borderStyle: 'solid', borderColor: 'var(--border-color)' }}>
          <p className="text-lg mb-4" style={{ color: 'var(--text-secondary)' }}>
            {isClientView ? 'No reflections available yet' : 'No reflections yet'}
          </p>
          <p className="text-sm mb-6" style={{ color: 'var(--text-tertiary)' }}>
            {isClientView
              ? 'Your coach will send you reflection questions here'
              : `Create your first weekly reflection to send to ${clientName}`
            }
          </p>
          {!isClientView && (
            <button
              onClick={() => setView('create')}
              className="px-6 py-3 rounded-lg font-semibold transition-all inline-flex items-center gap-2"
              style={{ backgroundColor: 'var(--accent)', color: '#ffffff' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--accent-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--accent)'}
            >
              <Plus className="w-5 h-5" />
              Generate Reflection
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {reflections.map((reflection) => (
            <div
              key={reflection.id}
              className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6 hover:border-[var(--accent)]/30 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {reflection.session_note
                        ? reflection.session_note.title
                        : reflection.week_start_date
                        ? `Week of ${new Date(reflection.week_start_date).toLocaleDateString()} - ${new Date(reflection.week_end_date).toLocaleDateString()}`
                        : 'Reflection'
                      }
                    </h3>
                    {getStatusBadge(reflection)}
                  </div>
                  <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                    {isClientView ? (
                      // For client view, show session date prominently
                      reflection.session_note?.session_date ? (
                        `Session: ${new Date(reflection.session_note.session_date).toLocaleDateString('en-US', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}`
                      ) : (
                        reflection.week_start_date ? (
                          `Week of ${new Date(reflection.week_start_date).toLocaleDateString()} - ${new Date(reflection.week_end_date).toLocaleDateString()}`
                        ) : (
                          `Sent ${new Date(reflection.sent_at).toLocaleDateString()}`
                        )
                      )
                    ) : (
                      // For coach view, show session date and sent/created date
                      <>
                        {reflection.session_note?.session_date && (
                          <span className="mr-4">
                            Session: {new Date(reflection.session_note.session_date).toLocaleDateString('en-US', {
                              weekday: 'short',
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        )}
                        {reflection.status === 'draft'
                          ? `Created ${new Date(reflection.created_at).toLocaleDateString()}`
                          : `Sent ${new Date(reflection.sent_at).toLocaleDateString()}`
                        }
                      </>
                    )}
                  </p>
                </div>
              </div>

              <p className="mb-4 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
                {reflection.ai_summary?.substring(0, 200)}...
              </p>

              <div className="flex items-center gap-3">
                {isClientView ? (
                  // Client view: Show answer button for sent reflections
                  reflection.status === 'sent' && (
                    <button
                      onClick={() => {
                        setSelectedReflectionId(reflection.id);
                        setView('edit');
                      }}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                      style={{ backgroundColor: 'var(--accent)', color: '#ffffff' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--accent-hover)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--accent)'}
                    >
                      {reflection.hasResponse ? 'View/Edit Answers' : 'Answer Questions'}
                    </button>
                  )
                ) : (
                  // Coach view: Show draft/sent buttons
                  reflection.status === 'draft' ? (
                    <button
                      onClick={() => {
                        setSelectedReflectionId(reflection.id);
                        setView('edit');
                      }}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                      style={{ backgroundColor: 'var(--accent)', color: '#ffffff' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--accent-hover)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--accent)'}
                    >
                      <SendIcon className="w-4 h-4" />
                      Continue & Send
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => handleViewResponse(reflection.id)}
                        className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                        style={{ backgroundColor: 'var(--accent)', color: '#ffffff' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--accent-hover)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--accent)'}
                      >
                        <Eye className="w-4 h-4" />
                        View Response
                      </button>
                      <button
                        onClick={() => {
                          setSelectedReflectionId(reflection.id);
                          setView('edit');
                        }}
                        className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                        style={{
                          backgroundColor: 'var(--bg-secondary)',
                          color: 'var(--text-primary)',
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          borderColor: 'var(--border-color)'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                      >
                        View Details
                      </button>
                    </>
                  )
                )}
              </div>

              {/* Show if client has responded - Coach view only */}
              {!isClientView && (
                <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
                  <div className="flex items-center gap-2 text-sm">
                    {reflection.status === 'sent' ? (
                      reflection.hasResponse ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-400" />
                          <span className="text-green-400">
                            Client responded {reflection.respondedAt ? new Date(reflection.respondedAt).toLocaleDateString() : ''}
                          </span>
                        </>
                      ) : (
                        <>
                          <Clock className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
                          <span style={{ color: 'var(--text-tertiary)' }}>
                            Awaiting client response
                          </span>
                        </>
                      )
                    ) : (
                      <span style={{ color: 'var(--text-tertiary)' }}>Not yet sent to client</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default WeeklyReflectionManager;
